import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import Card from '../components/Card'
import api from '../services/api'
import { getToken, clearAuth } from '../utils/userData'

const WellnessCollaborative = () => {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    joinFrequency: '',
    joinFrequencyOther: '',
    foundingMemberBandwidth: '',
    festivalInterest: '',
    festivalInterestOther: '',
    sessionFocus: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [checkingAuth, setCheckingAuth] = useState(true)

  useEffect(() => {
    // Check if user is authenticated as facilitator
    const token = getToken()
    if (!token) {
      setError('Please log in as a facilitator to submit this form.')
      setCheckingAuth(false)
      return
    }

    // Verify facilitator authentication by checking profile
    const verifyAuth = async () => {
      try {
        await api.get('/api/facilitators/profile')
        setCheckingAuth(false)
      } catch (err) {
        if (err.response?.status === 401 || err.response?.status === 403) {
          setError('Please log in as a facilitator to submit this form.')
          clearAuth()
        } else {
          setError('Failed to verify authentication. Please try again.')
        }
        setCheckingAuth(false)
      }
    }

    verifyAuth()
  }, [])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setIsSubmitting(true)

    // Validate required fields
    if (!formData.joinFrequency) {
      setError('Please select how frequently you would like to join us.')
      setIsSubmitting(false)
      return
    }

    if (formData.joinFrequency === 'others' && !formData.joinFrequencyOther.trim()) {
      setError('Please specify your preferred frequency.')
      setIsSubmitting(false)
      return
    }

    if (!formData.foundingMemberBandwidth) {
      setError('Please indicate if you have bandwidth for the founding member role.')
      setIsSubmitting(false)
      return
    }

    if (!formData.festivalInterest) {
      setError('Please indicate your interest in co-organizing the Wellness Festival.')
      setIsSubmitting(false)
      return
    }

    if (formData.festivalInterest === 'other' && !formData.festivalInterestOther.trim()) {
      setError('Please specify your interest in the Wellness Festival.')
      setIsSubmitting(false)
      return
    }

    if (!formData.sessionFocus || !formData.sessionFocus.trim()) {
      setError('Please share what type of sessions you would want the wellness collaborative to focus on.')
      setIsSubmitting(false)
      return
    }

    try {
      await api.post('/api/facilitators/wellness-collaborative', formData)
      setSuccess(true)
    } catch (err) {
      setError(
        err.response?.data?.message ||
          'Failed to submit form. Please try again.'
      )
    } finally {
      setIsSubmitting(false)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  if (checkingAuth) {
    return (
      <div className="bg-cream min-h-screen py-8">
        <div className="container mx-auto px-4">
          <Card className="max-w-2xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="text-4xl mb-4">⏳</div>
              <p className="text-gray-600">Verifying authentication...</p>
            </motion.div>
          </Card>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div className="bg-cream min-h-screen py-8">
        <div className="container mx-auto px-4">
          <Card className="max-w-2xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="text-6xl mb-4">✅</div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Thank you for your interest!
              </h2>
              <p className="text-gray-600 mb-6">
                Your submission has been received. We&apos;ll be in touch soon.
              </p>
              <button
                onClick={() => navigate(-1)}
                className="bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors"
              >
                Back to Meetup
              </button>
            </motion.div>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-cream min-h-screen py-8">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <div className="text-center mb-8">
            <button
              onClick={() => navigate(-1)}
              className="text-primary-600 hover:text-primary-700 mb-4 flex items-center gap-2 transition-colors justify-center"
            >
              <span>←</span> Back
            </button>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              We&apos;re exploring the idea of creating a{' '}
              <span className="text-primary-600">Wellness Collaborative!</span>
            </h1>
          </div>

          {/* Form */}
          <Card className="max-w-3xl mx-auto">
            {error && !isSubmitting && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
                {error}
                {error.includes('log in') && (
                  <div className="mt-2">
                    <button
                      onClick={() => navigate('/')}
                      className="text-red-700 underline font-semibold"
                    >
                      Go to Home
                    </button>
                  </div>
                )}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Question 1: Join Frequency */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  How frequently would you like to join us?
                </label>
                <p className="text-sm text-gray-600 mb-4">
                  This is a space for facilitators, therapists, and community leads to connect, learn, collaborate and co-create.
                </p>
                <div className="space-y-2">
                  {['weekly', 'fortnightly', 'monthly', 'unsure', 'others'].map((option) => (
                    <label
                      key={option}
                      className="flex items-center gap-3 p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      <input
                        type="radio"
                        name="joinFrequency"
                        value={option}
                        checked={formData.joinFrequency === option}
                        onChange={handleChange}
                        className="w-5 h-5 text-primary-600 border-gray-300 focus:ring-primary-500"
                      />
                      <span className="text-gray-700 capitalize">
                        {option === 'unsure' ? 'Unsure if I want to join' : option === 'others' ? 'Others (please specify)' : option}
                      </span>
                    </label>
                  ))}
                </div>
                {formData.joinFrequency === 'others' && (
                  <div className="mt-4">
                    <input
                      type="text"
                      name="joinFrequencyOther"
                      value={formData.joinFrequencyOther}
                      onChange={handleChange}
                      placeholder="Please specify..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                )}
              </div>

              {/* Question 2: Founding Member Bandwidth */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  We are looking for a core group of 5-6 founding members for this wellness collaborative. Do you have the bandwidth to take up this role?
                </label>
                <p className="text-sm text-gray-600 mb-4">
                  Approx. commitment would be 6h a week.
                </p>
                <div className="space-y-2">
                  {['yes', 'no', 'maybe'].map((option) => (
                    <label
                      key={option}
                      className="flex items-center gap-3 p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      <input
                        type="radio"
                        name="foundingMemberBandwidth"
                        value={option}
                        checked={formData.foundingMemberBandwidth === option}
                        onChange={handleChange}
                        className="w-5 h-5 text-primary-600 border-gray-300 focus:ring-primary-500"
                      />
                      <span className="text-gray-700 capitalize">{option}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Question 3: Festival Interest */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Are you interested in co-organizing a Wellness Festival in January 2026?
                </label>
                <p className="text-sm text-gray-600 mb-4">
                  Could be one or two days, could be 50-500 people, can be ticketed or free
                </p>
                <div className="space-y-2">
                  {[
                    { value: 'organizing', label: "Yes, I'm interested in organizing!" },
                    { value: 'facilitator', label: "Yes, i'm interested as a facilitator/stakeholder" },
                    { value: 'not-now', label: 'Not right now' },
                    { value: 'other', label: 'Other (please specify)' },
                  ].map((option) => (
                    <label
                      key={option.value}
                      className="flex items-center gap-3 p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      <input
                        type="radio"
                        name="festivalInterest"
                        value={option.value}
                        checked={formData.festivalInterest === option.value}
                        onChange={handleChange}
                        className="w-5 h-5 text-primary-600 border-gray-300 focus:ring-primary-500"
                      />
                      <span className="text-gray-700">{option.label}</span>
                    </label>
                  ))}
                </div>
                {formData.festivalInterest === 'other' && (
                  <div className="mt-4">
                    <input
                      type="text"
                      name="festivalInterestOther"
                      value={formData.festivalInterestOther}
                      onChange={handleChange}
                      placeholder="Please specify..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                )}
              </div>

              {/* Question 4: Session Focus */}
              <div>
                <label
                  htmlFor="sessionFocus"
                  className="block text-sm font-semibold text-gray-900 mb-2"
                >
                  Going forward, what type of sessions would you want this wellness collaborative to focus on?
                </label>
                <textarea
                  id="sessionFocus"
                  name="sessionFocus"
                  value={formData.sessionFocus}
                  onChange={handleChange}
                  rows={5}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                  placeholder="Share your thoughts on the type of sessions..."
                  required
                />
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </button>
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </Card>
        </motion.div>
      </div>
    </div>
  )
}

export default WellnessCollaborative

